import type { ThreadTopicSummary } from '@shared/types/thread-work';
import type { PlannedTodayRow } from './today-horizons';

/**
 * Tâche, séance, relais (21/10/2026): one launch of an app is one task in Mon
 * travail. The next actions an app proposes on its objects (N1–N2) are grouped
 * by the launch that last created or changed each object; « Faire » opens a
 * séance that goes through its objects one by one. An object whose next actions
 * are still being planned, or failed to be (`app_record_review:`), stays in its
 * launch too (28/10/2026): never one row per object. A lone topic stays a row.
 */
const OBJECT_ACTION_KEY = /^app_record:([0-9a-f-]{36}):/;
const OBJECT_PLANNING_KEY = /^app_record_review:([0-9a-f-]{36}):/;

export interface TodayLaunch {
  key: string;
  flowId: string;
  flowTitle: string;
  appKey: string | null;
  /** Null when the objects' launch is unknown or unreadable. */
  runId: string | null;
  launchedAt: string | null;
  /** The objects with an open action, first ones first (the planner's priorities). */
  recordIds: string[];
  /** Objects whose next actions are still being planned. */
  planning: string[];
  /** Planning topics that failed, to retry together. */
  failed: ThreadTopicSummary[];
  items: PlannedTodayRow[];
}

export type TodayEntry =
  | { kind: 'row'; item: PlannedTodayRow }
  | { kind: 'launch'; launch: TodayLaunch };

const actionObject = (item: PlannedTodayRow): string | null =>
  item.row.kind === 'app' && item.row.topic.visibleState === 'actionable'
    ? OBJECT_ACTION_KEY.exec(item.row.topic.semanticKey)?.[1] ?? null
    : null;

const planningObject = (item: PlannedTodayRow): string | null =>
  item.row.kind === 'app' ? OBJECT_PLANNING_KEY.exec(item.row.topic.semanticKey)?.[1] ?? null : null;

const objectOf = (item: PlannedTodayRow): string | null => actionObject(item) ?? planningObject(item);

const launchKey = (item: PlannedTodayRow, recordId: string): string | null => {
  if (item.row.kind !== 'app') return null;
  return `launch:${item.row.app.flowId}:${item.row.app.recordLaunches?.[recordId]?.runId ?? 'none'}`;
};

const unique = (ids: Array<string | null>): string[] => [...new Set(ids.filter((id): id is string => id !== null))];

/** Groups object topics by launch; a group takes the place of its first item. */
export const groupTodayEntries = (items: PlannedTodayRow[]): TodayEntry[] => {
  const groups = new Map<string, PlannedTodayRow[]>();
  items.forEach((item) => {
    const recordId = objectOf(item);
    const key = recordId ? launchKey(item, recordId) : null;
    if (key) groups.set(key, [...(groups.get(key) ?? []), item]);
  });
  const placed = new Set<string>();
  return items.flatMap((item): TodayEntry[] => {
    const recordId = objectOf(item);
    const key = recordId ? launchKey(item, recordId) : null;
    const group = key ? groups.get(key) : undefined;
    if (!key || !group || group.length < 2 || item.row.kind !== 'app') return [{ kind: 'row', item }];
    if (placed.has(key)) return [];
    placed.add(key);
    const { app } = item.row;
    const launch = app.recordLaunches?.[recordId!] ?? null;
    const recordIds = unique(group.map(actionObject));
    const planningItems = group.filter((member) => planningObject(member) && !recordIds.includes(planningObject(member)!));
    const failed = planningItems.filter((member) => member.row.topic.preparation?.status === 'failed');
    return [{
      kind: 'launch',
      launch: {
        key, flowId: app.flowId, flowTitle: app.flowTitle, appKey: app.appKey ?? null,
        runId: launch?.runId ?? null, launchedAt: launch?.launchedAt ?? null,
        recordIds,
        planning: unique(planningItems.filter((member) => !failed.includes(member)).map(planningObject)),
        failed: failed.map((member) => member.row.topic),
        items: group,
      },
    }];
  });
};

/** The séance value of a launch in the object's address; `tout` for objects without a known launch. */
export const SESSION_ALL = 'tout';

/** Where « Faire » leads: the first object of the launch, in séance. */
export const launchSessionHref = (launch: TodayLaunch): string => {
  const first = launch.recordIds[0];
  if (!launch.appKey || !first) return `/tools/${encodeURIComponent(launch.flowId)}?suite=1`;
  return `/apps/${encodeURIComponent(launch.appKey)}/o/${encodeURIComponent(first)}?seance=${encodeURIComponent(launch.runId ?? SESSION_ALL)}`;
};
